import { fetchAllInstances } from '@/requests/api/fetch-all-instances';
import type {
  BrfApartment,
  BrfCompany,
  Building,
  Company,
} from '@pigello/pigello-matrix';
import { chunkIds } from '../utils';
import type { TCustomList } from './types';

interface RealEstateWithBrfCompany extends NonNullable<Building['realestate']> {
  readonly brfCompany?: BrfCompany;
}

function mergeOwnedByOntoBuilding(
  building: Building,
  companyById: Map<string, Company>
): Building {
  const realestate = building.realestate;
  const ownedById = realestate?.ownedBy?.id;
  if (!realestate || !ownedById) return building;

  const company = companyById.get(ownedById);
  if (!company) return building;

  return {
    ...building,
    realestate: {
      ...realestate,
      ownedBy: company,
    },
  };
}

function mergeBrfCompanyOntoBuilding(
  building: Building,
  brfCompanyByCompanyId: Map<string, BrfCompany>
): Building {
  const realestate = building.realestate;
  const ownedById = realestate?.ownedBy?.id;
  if (!realestate || !ownedById) return building;

  const brfCompany = brfCompanyByCompanyId.get(ownedById);
  if (!brfCompany) return building;

  const company = realestate.ownedBy ?? brfCompany.company;

  const realestateWithBrf: RealEstateWithBrfCompany = {
    ...realestate,
    brfCompany: {
      ...brfCompany,
      company,
    },
  };

  return {
    ...building,
    realestate: realestateWithBrf as Building['realestate'],
  };
}

export async function handleCustomBrfApartmentNestedCase(
  listResponse: TCustomList<BrfApartment>
): Promise<TCustomList<BrfApartment>> {
  const buildingIds = listResponse.data.reduce<Set<string>>(
    (acc, brfApartment) => {
      if (brfApartment.building?.id) {
        acc.add(brfApartment.building.id);
      }
      return acc;
    },
    new Set()
  );

  if (buildingIds.size === 0) return listResponse;

  const buildingMap = new Map<string, Building>();
  const chunkedBuildingIds = chunkIds(buildingIds);

  const buildingResults = await Promise.all(
    chunkedBuildingIds.map((chunk) =>
      fetchAllInstances<Building>({
        modelName: 'building',
        filters: { id: { __in: chunk.join(',') } },
        nested: ['realestate'],
      })
    )
  );

  for (const res of buildingResults) {
    for (const building of res.list) {
      buildingMap.set(building.id, building);
    }
  }

  const ownedByIds = new Set<string>();
  for (const building of buildingMap.values()) {
    const id = building.realestate?.ownedBy?.id;
    if (id) ownedByIds.add(id);
  }

  const companyById = new Map<string, Company>();
  if (ownedByIds.size > 0) {
    const chunkedCompanyIds = chunkIds(ownedByIds);
    const companyResults = await Promise.all(
      chunkedCompanyIds.map((chunk) =>
        fetchAllInstances<Company>({
          modelName: 'company',
          filters: { id: { __in: chunk.join(',') } },
        })
      )
    );
    for (const res of companyResults) {
      for (const company of res.list) {
        companyById.set(company.id, company);
      }
    }
  }

  const brfCompanyByCompanyId = new Map<string, BrfCompany>();
  if (ownedByIds.size > 0) {
    const chunkedForBrf = chunkIds(ownedByIds);
    const brfCompanyResults = await Promise.all(
      chunkedForBrf.map((chunk) =>
        fetchAllInstances<BrfCompany>({
          modelName: 'brfcompany',
          filters: { company: { __in: chunk.join(',') } },
          nested: ['company'],
        })
      )
    );
    for (const res of brfCompanyResults) {
      for (const brfCompany of res.list) {
        const companyId = brfCompany.company?.id;
        if (companyId) {
          brfCompanyByCompanyId.set(companyId, brfCompany);
        }
      }
    }
  }

  for (const [id, building] of buildingMap) {
    const withOwnedBy = mergeOwnedByOntoBuilding(building, companyById);
    buildingMap.set(
      id,
      mergeBrfCompanyOntoBuilding(withOwnedBy, brfCompanyByCompanyId)
    );
  }

  return {
    ...listResponse,
    data: listResponse.data.map((brfApartment) => {
      const buildingId = brfApartment.building?.id;
      if (!buildingId) return brfApartment;

      const fullBuilding = buildingMap.get(buildingId);
      if (!fullBuilding) return brfApartment;

      return {
        ...brfApartment,
        building: fullBuilding,
      };
    }),
  };
}
